/**
 * Start a new design from an existing one (D9): a copy of its working draft,
 * or a fork of one of its published versions.
 *
 * The two differ in what they promise. A fork records the version it branched
 * from, so "variant B came from A at v3" stays answerable; it needs something
 * published to point at. A copy takes the draft as it stands, unpublished
 * edits included, and records no lineage, because a draft is not citable.
 * Both keep every identifier (D10), so a script ported across still resolves.
 *
 * Opened from the schematic editor (the usual place: the design on screen is
 * the starting point), from a project's design list, and from a project card,
 * which offers the project's designs to choose from.
 */
import { useEffect, useId, useState } from "react";
import { api, type Design, type Project, type VersionSummary } from "../api/client";
import { ConfirmDialog } from "../components";

export type StartMode = "copy" | "fork";

function defaultName(source: string, mode: StartMode): string {
  return `${source} ${mode === "copy" ? "copy" : "fork"}`;
}

export function StartFromDesignDialog({
  design: initial,
  choices,
  onCreated,
  onCancel,
}: {
  design: Design;
  /** Designs the source may be switched to, `design` among them. */
  choices?: readonly Design[];
  /** The new design, to open. */
  onCreated: (created: Design) => void;
  onCancel: () => void;
}) {
  const [design, setDesign] = useState(initial);
  const [versions, setVersions] = useState<VersionSummary[] | null>(null);
  const [projects, setProjects] = useState<Project[] | null>(null);
  const [mode, setMode] = useState<StartMode>("copy");
  const [versionId, setVersionId] = useState("");
  const [name, setName] = useState(defaultName(design.name, "copy"));
  const [named, setNamed] = useState(false);
  const [projectId, setProjectId] = useState(design.project_id);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const modeName = useId();
  const sourceField = useId();
  const versionField = useId();
  const projectField = useId();

  useEffect(() => {
    let live = true;
    setVersions(null);
    api
      .listVersions(design.id)
      .then((listed) => {
        if (!live) return;
        const newest = [...listed].sort((a, b) => b.number - a.number);
        setVersions(newest);
        setVersionId(newest[0]?.id ?? "");
        // Nothing to fork in this one: back to a copy.
        if (newest.length === 0) setMode("copy");
      })
      .catch((caught) => live && setError(caught));
    return () => {
      live = false;
    };
  }, [design.id]);

  useEffect(() => {
    let live = true;
    // Somewhere to put it: a project the reader can see. The server decides
    // whether they may write there, and says so if not.
    api
      .listProjects()
      .then((listed) => live && setProjects(listed))
      .catch(() => live && setProjects([]));
    return () => {
      live = false;
    };
  }, []);

  const published = versions !== null && versions.length > 0;
  const choose = (next: StartMode) => {
    setMode(next);
    // A name the designer typed is theirs; the suggestion follows the choice.
    if (!named) setName(defaultName(design.name, next));
  };
  const pick = (id: string) => {
    const next = choices?.find((candidate) => candidate.id === id);
    if (!next) return;
    setDesign(next);
    setError(null);
    if (!named) setName(defaultName(next.name, mode));
  };

  const valid = name.trim().length > 0 && (mode === "copy" || versionId !== "");
  const submit = async () => {
    if (!valid || busy) return;
    setBusy(true);
    setError(null);
    const target = projectId === design.project_id ? undefined : projectId;
    try {
      const created =
        mode === "copy"
          ? await api.copyDesign(design.id, name.trim(), target)
          : await api.forkDesign(design.id, versionId, name.trim(), target);
      onCreated(created);
    } catch (caught) {
      setError(caught);
      setBusy(false);
    }
  };

  // Always list the design's own project, even before the list arrives.
  const projectOptions =
    projects === null || !projects.some((project) => project.id === design.project_id)
      ? [{ id: design.project_id, name: "This project" }, ...(projects ?? [])]
      : projects;

  return (
    <ConfirmDialog
      title={`New design from ${design.name}`}
      confirmLabel={mode === "copy" ? "Copy design" : "Fork design"}
      busyLabel={mode === "copy" ? "Copying…" : "Forking…"}
      danger={false}
      focusField
      busy={busy}
      confirmDisabled={!valid}
      error={error}
      onConfirm={() => void submit()}
      onCancel={onCancel}
    >
      <form
        className="stack"
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        {choices && choices.length > 1 && (
          <div className="stack" style={{ gap: "0.35rem" }}>
            <label htmlFor={sourceField}>Design</label>
            <select id={sourceField} value={design.id} onChange={(event) => pick(event.target.value)}>
              {choices.map((choice) => (
                <option key={choice.id} value={choice.id}>
                  {choice.name}
                </option>
              ))}
            </select>
          </div>
        )}
        <fieldset className="stack field-group">
          <legend>Start from</legend>
          <label className="row">
            <input type="radio" name={modeName} checked={mode === "copy"} onChange={() => choose("copy")} />
            Copy the working draft
          </label>
          <span className="muted field-help">
            Everything on the schematic now, unpublished changes included. A fresh start: no link back to{" "}
            {design.name}.
          </span>
          <label className="row">
            <input
              type="radio"
              name={modeName}
              checked={mode === "fork"}
              disabled={!published}
              onChange={() => choose("fork")}
            />
            Fork a published version
          </label>
          <span className="muted field-help">
            {versions === null
              ? "Looking for published versions…"
              : published
                ? `A competing variant. It records which version of ${design.name} it branched from.`
                : `${design.name} has no published version to fork yet. Publish one, or copy the draft.`}
          </span>
        </fieldset>
        {mode === "fork" && published && (
          <div className="stack" style={{ gap: "0.35rem" }}>
            <label htmlFor={versionField}>Version</label>
            <select id={versionField} value={versionId} onChange={(event) => setVersionId(event.target.value)}>
              {versions!.map((version) => (
                <option key={version.id} value={version.id}>
                  v{version.number}
                  {version.message ? `: ${version.message}` : ""}
                </option>
              ))}
            </select>
          </div>
        )}
        <label className="stack" style={{ gap: "0.35rem" }}>
          Name
          <input
            value={name}
            maxLength={200}
            onChange={(event) => {
              setName(event.target.value);
              setNamed(true);
            }}
          />
        </label>
        <div className="stack" style={{ gap: "0.35rem" }}>
          <label htmlFor={projectField}>Project</label>
          <select id={projectField} value={projectId} onChange={(event) => setProjectId(event.target.value)}>
            {projectOptions.map((project) => (
              <option key={project.id} value={project.id}>
                {project.id === design.project_id ? `${project.name} (this project)` : project.name}
              </option>
            ))}
          </select>
          {projectId !== design.project_id && (
            <span className="muted field-help">
              A design that uses this project's own blocks or templates can only be copied within it.
            </span>
          )}
        </div>
      </form>
    </ConfirmDialog>
  );
}

/**
 * The same, from a project card: one of the project's designs is the source.
 * The design edited last is offered first, as the card's thumbnail shows it.
 */
export function StartFromProjectDialog({
  projectId,
  leadDesignId,
  onCreated,
  onCancel,
}: {
  projectId: string;
  leadDesignId?: string | null;
  onCreated: (created: Design) => void;
  onCancel: () => void;
}) {
  const [designs, setDesigns] = useState<Design[] | null>(null);
  const [error, setError] = useState<unknown>(null);
  useEffect(() => {
    let live = true;
    api
      .listDesigns(projectId)
      .then((listed) => live && setDesigns([...listed].sort((a, b) => a.name.localeCompare(b.name))))
      .catch((caught) => live && setError(caught));
    return () => {
      live = false;
    };
  }, [projectId]);

  if (error !== null || (designs !== null && designs.length === 0)) {
    return (
      <ConfirmDialog
        title="New design from this project"
        confirmLabel="Copy design"
        danger={false}
        confirmDisabled
        error={error}
        onConfirm={() => undefined}
        onCancel={onCancel}
      >
        <p style={{ margin: 0 }}>{error === null ? "This project has no designs to start from." : "The designs could not be listed."}</p>
      </ConfirmDialog>
    );
  }
  if (designs === null) return null;
  const lead = designs.find((design) => design.id === leadDesignId) ?? designs[0]!;
  return <StartFromDesignDialog design={lead} choices={designs} onCreated={onCreated} onCancel={onCancel} />;
}
